// FLAGS: tenancy
// Which companies a private space is open to. This selection is the eligibility set.
import { X } from "lucide-react";

import { Select, fieldClass } from "@src/components/spaces/ui";
import { Employer } from "@src/models/spaces";

interface CompanyPickerProps {
  id: string;
  invalid?: boolean;
  employers: Employer[];
  selectedIds: string[];
  disabled?: boolean;
  onChange: (employerIds: string[]) => void;
}

/**
 * Add-then-list rather than a multi-select box: a scheduler picks one company at
 * a time, and the ones already chosen have to stay readable, because this is the
 * set that decides who may register. A native multi-select hides that behind
 * scroll and ctrl-click.
 */
const CompanyPicker = ({
  id,
  invalid = false,
  employers,
  selectedIds,
  disabled = false,
  onChange,
}: CompanyPickerProps) => {
  const selected = selectedIds
    .map((employerId) =>
      employers.find((employer) => employer.id === employerId),
    )
    .filter((employer): employer is Employer => employer !== undefined);

  const remaining = employers.filter(
    (employer) => !selectedIds.includes(employer.id),
  );

  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {selected.map((employer) => (
            <li key={employer.id}>
              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 py-1 pl-3 pr-1 text-sm text-indigo-800">
                {employer.name}
                <button
                  type="button"
                  aria-label={`Remove ${employer.name}`}
                  className="rounded-full p-0.5 text-indigo-700 hover:bg-indigo-100"
                  onClick={() =>
                    onChange(
                      selectedIds.filter(
                        (employerId) => employerId !== employer.id,
                      ),
                    )
                  }
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {remaining.length > 0 && (
        <Select
          id={id}
          aria-invalid={invalid}
          className={fieldClass}
          value=""
          disabled={disabled}
          onChange={(e) => onChange([...selectedIds, e.target.value])}
        >
          <option value="">
            {selected.length === 0 ? "Select a company" : "Add another company"}
          </option>
          {remaining.map((employer) => (
            <option key={employer.id} value={employer.id}>
              {employer.name}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
};

export default CompanyPicker;
